// SearchSection.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios"; // Ensure axios is imported
import { connect } from "react-redux";
import AutoCompleteResult from "./AutoCompleteResult";
import SearchService from "../../services/SearchService";
import FloatingInputLabel from "./FloatingInputLabel";
import Spinner from "react-bootstrap/Spinner";
import PropTypes from "prop-types";
import "./FloatingInputLabel.scss"; // Ensure styles are imported
import Swal from "sweetalert2";

const SearchSection = (props) => {
   const [comChoice, setComChoice] = useState(null);
   const [comChoiceLabel, setComChoiceLabel] = useState("");
   const [isLoading, setIsLoading] = useState(false);
   const [sectionChoice, setSectionChoice] = useState([]);
   const [parcelleChoice, setParcelleChoice] = useState([]);
   const [query, setQuery] = useState("");
   const [prefixe, setPrefixe] = useState();
   const [section, setSection] = useState();
   const [parcelle, setParcelle] = useState();
   const [results, setResults] = useState([]);

   const sectionRef = useRef(null);
   const parcelleRef = useRef(null);

   const searchService = new SearchService();

   const [bulkParcellesInput, setBulkParcellesInput] = useState('');
   const [bulkParcelles, setBulkParcelles] = useState([]);

   // on mount, load saved commune if any
   useEffect(() => {
      const savedCode = localStorage.getItem('comChoice');
      const savedLabel = localStorage.getItem('comChoiceLabel');
      if (savedCode && savedLabel) {
         setComChoice(savedCode);
         setComChoiceLabel(savedLabel);
         // fetch sections & geometry for that commune
         getSectionsByCom(savedCode);
         getCommGeom(savedCode);
      }
   }, []);

   // Initialize with com if exists
   useEffect(() => {
      const initResult = async () => {
         if (props.levelTerr.trim() === "com") {
            try {
               const codesTerr = props?.entityCodeTerr ? JSON.parse(props.entityCodeTerr) : null;
               const comCode = codesTerr ? codesTerr[0].code : " ";
               if (comCode) {
                  setComChoice(comCode);
                  setComChoiceLabel(props.userComLabel);
                  await getSectionsByCom(comCode);
                  await getCommGeom(comCode);
               }
            } catch (error) {
               console.error("Error initializing SearchSection:", error);
            }
         }
      };
      initResult();
      // Removed jQuery tooltip initialization
   }, [props.levelTerr, props.entityCodeTerr]);

   // Handle input changes
   const handleChange = useCallback((e) => {
      setSectionChoice([]);
      setParcelleChoice([]);
      props.cleanVectorLayer(true);
      setQuery(e?.target?.value);
   }, [props]);

   const handleSectionChange = useCallback(() => {
      const sectionValue = sectionRef.current.value;
      setParcelleChoice([]);
      setSection(sectionValue);
      props.cleanVectorLayer(true);
      getSectionGeom(sectionValue);
   }, [props, comChoice]);

   const handleParcelleChange = useCallback((e) => {
      const selectedValue = e.target.value;
      if (props.searchType === "histo") {
         searchService.getHistoriqueParcelles(comChoice, section, selectedValue);
      } else {
         setParcelle(selectedValue);
         getParcelleGeom(comChoice, prefixe, section, selectedValue);
         console.log('handleParcelleChange', comChoice, prefixe, section, selectedValue);
         props.setCurrentSection("data");
      }
   }, [comChoice, section, prefixe, props]);

   // Watch for section changes to fetch parcelles
   useEffect(() => {
      if (section && section !== "Sections") {
         getParcelleByComPrefixSection(section);
      }
   }, [section]);

   // Watch for filter tool status changes
   useEffect(() => {
      if (props.filterToolStat) {
         setSectionChoice([]);
         setParcelleChoice([]);
         props.cleanVectorLayer(true);
      }
   }, [props.filterToolStat, props]);

   const handleBulkParcellesSubmit = async (e) => {
      e.preventDefault();
      if (!comChoice) {
         Swal.fire({
            icon: 'warning',
            title: 'Erreur',
            text: "Veuillez d'abord sélectionner une commune."
         });
         return;
      }

      // parse & validate codes like "0A0001", "AD0123", "AI0013"
      const parcels = bulkParcellesInput
         .split(/[;,]+/)
         .map(p => p.trim().toUpperCase())
         .filter(p => /^[A-Z0-9]{2}\d{3,4}$/.test(p));

      if (!parcels.length) {
         Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: "Saisissez des codes valides (ex : 0A0001, AD0123, AI0013)."
         });
         return;
      }

      setBulkParcelles(parcels);
      setIsLoading(true);

      try {
         // Fetch all in parallel, isolating each error
         const features = (
            await Promise.all(
               parcels.map(async (code) => {
                  const sectionCode = code.slice(0, 2);        // "0A" or "AD"
                  const rawNum = code.slice(2);           // "0001" or "0123"
                  const numero = rawNum.replace(/^0+/, '') || '0';
                  const paddedNum = rawNum.padStart(4, '0');

                  // match your single-parcel logic: strip leading zero
                  const sectionParam = sectionCode.startsWith('0')
                     ? sectionCode[1]
                     : sectionCode;

                  // look up the real prefixe; fall back to prefixe state or "000"
                  const entry = sectionChoice.find(s => s.getsection === sectionCode);
                  const prefixToUse = entry?.prefixe ?? prefixe ?? '000';

                  const fullId = `${comChoice}${prefixToUse}${sectionCode}${paddedNum}`;

                  try {
                     const resp = await axios.post('/Api/geodata/geom/parcelle', {
                        code_com: comChoice,
                        prefixe: prefixToUse,
                        section: sectionParam,
                        parcelle: numero,
                     });
                     const raw = resp.data[0]?.json_build_object;
                     if (!raw) {
                        console.warn('Pas de géométrie pour', fullId);
                        return null;
                     }
                     const jsonObj = JSON.parse(raw);
                     const feat = jsonObj.type === 'Feature'
                        ? { ...jsonObj, properties: { ...jsonObj.properties, _fullId: fullId } }
                        : { type: 'Feature', geometry: jsonObj, properties: { _fullId: fullId } };
                     feat.id = fullId;
                     return feat;
                  } catch (err) {
                     console.error('Erreur géométrie pour', fullId, err);
                     return null;
                  }
               })
            )
         ).filter(f => f !== null);

         if (features.length === 0) {
            Swal.fire({
               icon: 'warning',
               title: 'Aucune géométrie trouvée',
               text: 'Aucun des codes saisis n’a retourné de géométrie valide.'
            });
         } else {
            const fc = { type: 'FeatureCollection', features };
            props.cleanVectorLayer(true);
            props.updateVectorLayer(fc, 'parcelle');
            props.togglePanelTab('geoData');
            features.forEach(f => props.findParcelleObject(f.properties._fullId));
         }

      } catch (err) {
         console.error('Erreur multi-parcelles :', err);
      } finally {
         setIsLoading(false);
         props.setCurrentSection('data');
      }
   };


   // Fetch commune results based on query
   const getResults = async () => {
      const minCharacters = props.minCharacters || 0;
      if (query?.length < minCharacters) return;

      try {
         setIsLoading(true);
         const response = await axios.post("Api/geodata/commune_ref", { search: query });
         setResults(response.data.data);
      } catch (error) {
         console.error("Error fetching commune results:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Handle selection of a commune
   const selectResult = async (label, insee) => {
      try {
         setIsLoading(true);
         const response = await axios.get(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(label)}&type=municipality`);
         if (response?.data?.features.length) {
            const currentResult = response.data.features[0];
            setComChoice(currentResult.properties.id);
            setComChoiceLabel(currentResult.properties.label);
            localStorage.setItem('comChoice', currentResult.properties.id);
            localStorage.setItem('comChoiceLabel', currentResult.properties.label);
            props.updateCenter(currentResult.geometry.coordinates);
            await getSectionsByCom(currentResult.properties.id);
            await getCommGeom(currentResult.properties.id);
         }
      } catch (error) {
         console.error("Error selecting commune:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Fetch sections based on commune code
   const getSectionsByCom = async (cityCode) => {
      setComChoice(cityCode);
      const params = {
         code_com: cityCode,
      };
      try {
         props.loading(true);
         const response = await axios.post("/Api/geodata/getPrefixeByCommune", params);
         const sections = response.data[0];
         sections.unshift({ commune: null, getsection: "selection", prefixe: "000", section: "Choisissez" });
         setSectionChoice(sections);
      } catch (error) {
         console.error("Error fetching sections:", error);
      } finally {
         props.loading(false);
      }
   };

   // Fetch parcelles based on commune and section
   const getParcelleByComPrefixSection = async (sectionCode) => {
      const formattedSection = sectionCode.length < 2 ? "0" + sectionCode : sectionCode;
      setSection(formattedSection);
      setPrefixe("000");

      const params = {
         code_com: comChoice,
         prefixe: "000",
         section: formattedSection,
      };
      const url =
         props.searchType === "histo"
            ? "/Api/geodata/vmap/historique/old_parcelles"
            : "/Api/geodata/getParcelleByCommunePrefixSection";


      if (!sectionCode.includes("ecti")) {
         try {
            const response = await axios.post(url, params);
            const parcelles = response.data;
            parcelles.unshift({ getnumero: null, numero: "Choisissez" });
            setParcelleChoice(parcelles);
         } catch (error) {
            console.error("Error fetching parcelles:", error);
         }
      }
   };

   // Fetch commune geometry
   const getCommGeom = async (codeCom) => {
      const params = { code_com: codeCom };
      try {
         setIsLoading(true);
         const response = await axios.post("/Api/geodata/geom/commune", params);
         if (response.data[0]) {
            props.updateVectorLayer(JSON.parse(response.data[0].json_build_object), "commune");
            props.setCurrentCom(codeCom);
         }
      } catch (error) {
         console.error("Error fetching commune geometry:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Fetch section geometry
   const getSectionGeom = async (sectionCode) => {
      const formattedSection = sectionCode.substring(0, 1) === "0" ? sectionCode.substring(1, 2) : sectionCode;
      const params = {
         code_com: comChoice,
         prefixe: "000",
         section: formattedSection,
      };
      try {
         setIsLoading(true);
         const response = await axios.post("/Api/geodata/geom/section", params);
         props.updateVectorLayer(JSON.parse(response.data[0].json_build_object), "section");
      } catch (error) {
         console.error("Error fetching section geometry:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Fetch parcelle geometry
   const getParcelleGeom = async (com, prefixe, sectionCode, numero) => {
      const formattedSection = sectionCode.substring(0, 1) === "0" ? sectionCode.substring(1, 2) : sectionCode;
      const params = {
         code_com: com,
         prefixe: "000",
         section: formattedSection,
         parcelle: numero,
      };
      try {
         setIsLoading(true);
         const response = await axios.post("/Api/geodata/geom/parcelle", params);
         const parcelleData = JSON.parse(response.data[0].json_build_object);
         props.updateVectorLayer(parcelleData, "parcelle");
         props.togglePanelTab("geoData");
         props.findParcelleObject(parcelleData.id);
         console.log('findParcelleObject', parcelleData.id);
         props.cleanVectorLayer(false);
      } catch (error) {
         console.error("Error fetching parcelle geometry:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Reset commune selection
   const handleResetCom = useCallback(() => {
      localStorage.removeItem('comChoice');
      localStorage.removeItem('comChoiceLabel');
      setComChoice(null);
      setComChoiceLabel('');
      setSectionChoice([]);
      setParcelleChoice([]);
      props.setCurrentCom(null);
   }, [props]);

   // Render options for sections
   const renderSectionOption = (item, key) => (
      <option key={key} value={item.getsection}>
         {item.section}
      </option>
   );

   // Render options for parcelles
   const renderParcelleOption = (item, key) => (
      <option key={key} value={item.numero}>
         {item.numero}
      </option>
   );

   return (
      <div className="d-flex flex-column">
         <div className="my-2">
            {!comChoice ? (
               <div className="form-group position-relative mb-3 mb-md-0 w-100">
                  <FloatingInputLabel
                     label="Commune INSEE"
                     id="comSearchInput"
                     className="input-proprioC"
                     onChange={handleChange}
                     onKeyUp={getResults}
                     value={query}
                     name="comSearchInput"
                  />
                  {isLoading && (
                     <Spinner
                        animation="border"
                        size="sm"
                        className="top-50"
                     />
                  )}
                  <AutoCompleteResult results={results} selectResult={selectResult} />
               </div>
            ) : comChoiceLabel ? (
               <div className="d-flex mt-3 bg-std-dark align-items-center w-100">
                  <input
                     type="text"
                     value={`${comChoiceLabel} (${comChoice})`}
                     aria-label="Propriétaire"
                     className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1 floating-input-value"
                     readOnly
                  />
                  <button
                     className="btn btn-link border-0 ms-2"
                     type="button"
                     onClick={handleResetCom}
                     aria-label="Clear"
                  >
                     <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
                  </button>
               </div>
            ) : (
               <div className="d-flex mt-3 bg-std-dark align-items-center w-100">
                  <input
                     type="text"
                     value={comChoice}
                     aria-label="Propriétaire"
                     className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1 floating-input-value"
                     readOnly
                  />
                  <button
                     className="btn btn-link border-0 ms-2"
                     type="button"
                     onClick={handleResetCom}
                     aria-label="Clear"
                  >
                     <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
                  </button>
               </div>
            )}
         </div>

         {sectionChoice.length <= 0 ? (
            <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
               <div className="did-floating-label-content form-group mb-3 mb-md-0 w-100 section-input-container">
                  <input
                     type="text"
                     aria-label="Section"
                     className="did-floating-input-m form-control filter-tools-input w-100"
                     placeholder=""
                     disabled
                     style={{ opacity: "0.3" }}
                  />
                  <label className="did-floating-label-m">Section</label>
               </div>
            </div>
         ) : (
            <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
               <div className="did-floating-label-content form-group mb-3 mb-md-0 w-100 section-select-container">
                  <select
                     id="sectionControlSelect"
                     className="did-floating-input-m form-control filter-tools-input "
                     ref={sectionRef}
                     value={section}
                     onChange={handleSectionChange}
                  >
                     {sectionChoice.map(renderSectionOption)}
                  </select>
                  <label className="did-floating-label-m">Section</label>
               </div>
            </div>
         )}
         {parcelleChoice.length <= 0 ? (
            <div className="form-group my-2 mb-2 d-flex flex-column flex-md-row flex-grow-1">
               <div className="did-floating-label-content form-group mb-3 mb-md-0 w-100 parcelle-input-container">
                  <input
                     type="text"
                     aria-label="Parcelle"
                     className="did-floating-input-m form-control filter-tools-input w-100"
                     placeholder=""
                     disabled
                     style={{ opacity: "0.3" }}
                  />
                  <label className="did-floating-label-m">Parcelle</label>
               </div>
            </div>
         ) : (
            <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
               <div className="did-floating-label-content form-group mb-3 mb-md-0 parcelle-select-container w-100">
                  <select
                     className="did-floating-input-m form-control filter-tools-input"
                     id="parcelleControlSelect"
                     ref={parcelleRef}
                     value={parcelle}
                     onChange={handleParcelleChange}
                  >
                     {parcelleChoice.map(renderParcelleOption)}
                  </select>
                  <label className="did-floating-label-m">Parcelle</label>
               </div>
            </div>
         )}
         <div className="my-2 d-flex flex-column flex-grow-1 align-items-center text-center" style={{ fontWeight: '700', color: '#58D2C3', textAlign: 'center' }}>
            Recherche multiple (parcelles séparées par ',' ou ';')
         </div>
         <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
            <textarea
               className="form-control filter-tools-input w-100"
               rows="3"
               placeholder="Ex : 0A0001, 0A1551; 0A1277"
               value={bulkParcellesInput}
               onChange={(e) => setBulkParcellesInput(e.target.value)}
            />
         </div>
         <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
            <button
               type="button" // <-- Important to prevent default submit behavior
               className="btn btn-primary w-100 text-white fw-bold text-uppercase"
               style={{ height: 35, fontWeight: 700 }}
               onClick={handleBulkParcellesSubmit}
               disabled={!bulkParcellesInput.trim()}
            >
               RECHERCHER
            </button>
         </div>
      </div>
   );
};

// Prop type checking
SearchSection.propTypes = {
   levelTerr: PropTypes.string.isRequired,
   entityCodeTerr: PropTypes.string,
   com: PropTypes.string,
   loading: PropTypes.func.isRequired,
   updateCenter: PropTypes.func.isRequired,
   updateVectorLayer: PropTypes.func.isRequired,
   togglePanelTab: PropTypes.func.isRequired,
   cleanVectorLayer: PropTypes.func.isRequired,
   setCurrentCom: PropTypes.func.isRequired,
   setCurrentSection: PropTypes.func.isRequired,
   searchType: PropTypes.string,
   findParcelleObject: PropTypes.func.isRequired,
   filterToolStat: PropTypes.bool,
};

// Default props
SearchSection.defaultProps = {
   entityCodeTerr: null,
   com: "",
   searchType: "",
   filterToolStat: false,
   comChoiceLabel: "",
};

const mapStateToProps = (state) => ({
   levelTerr: state.MeReducer?.entity?.level_terr,
   userCom: state.MapReducer.currentCom,
   userComLabel: state.MapReducer.currentComLabel,
   filterToolStat: state.SearchReducer.filterToolStat,
   entityCodeTerr: state.MeReducer.entity.codes_terr,
});

const mapDispatchToProps = (dispatch) => ({
   updateCenter: (e, z = 17) =>
      dispatch({
         type: "UPDATE_CENTER_MAP",
         data: { centerMap: e, zoom: z },
      }),
   findParcelleObject: (e) =>
      dispatch({
         type: "FIND_PARCELLE_ON_LAYER",
         data: { parcelleCode: e },
      }),
   updateVectorLayer: (geoJsonData, typeTerr) =>
      dispatch({
         type: "UPDATE_VECTOR_LAYER",
         data: { geoJsonData, typeTerr },
      }),
   togglePanelTab: (currentTab) =>
      dispatch({
         type: "TOGGLE_PANEL_TAB",
         data: { currentTab },
      }),
   cleanVectorLayer: (e) =>
      dispatch({
         type: "CLEAR_VECTOR_LAYER",
         data: e,
      }),
   setCurrentCom: (e) =>
      dispatch({
         type: "SET_CURRENT_COM",
         data: { currentCom: e },
      }),
   setCurrentSection: (e) =>
      dispatch({
         type: "SHOW_CURRENT_SECTION",
         data: e,
      }),
   loading: (isLoading) =>
      dispatch({
         type: "SET_LOADING",
         data: { isLoading },
      }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchSection);
